/**
 * ExamTab — Prüfungssimulation tab content.
 * Previously inline in App.tsx (lines 813-882).
 */
import React from 'react';
import { Info, Target } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './UIComponents';
import ReadinessCard from './ReadinessCard';
import { getConfidenceTrend } from './ConfidenceCheck';
import { useUIStore } from '../stores';
import { ApiClient } from '../services/apiClient';
import type { UserProfile } from '../../shared/types';

interface ExamTabProps {
  user: UserProfile;
  isPremium: boolean;
  onStartExam: () => void;
}

const ExamTab: React.FC<ExamTabProps> = ({ user, isPremium, onStartExam }) => {
  const { t } = useTranslation();
  const setPremiumTrigger = useUIStore((s) => s.setPremiumTrigger);

  const history = user.simulationHistory || [];
  const trend = getConfidenceTrend();
  const examPoolSize = React.useMemo(() => ApiClient.getExamPoolSize(user), [user]);

  return (
    <div
      className="p-4 flex flex-col items-center text-center gap-3"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 5.5rem)' }}
    >
      {/* Compact hero: icon + title + subtitle stacked horizontally to free vertical space */}
      <div className="flex items-center gap-3 w-full">
        <div className="w-14 h-14 bg-orange-100 dark:bg-orange-900/20 rounded-2xl flex items-center justify-center flex-shrink-0">
          <Target size={28} className="text-primary" />
        </div>
        <div className="text-left flex-1 min-w-0">
          <h2 className="text-xl font-bold text-[var(--f-text)] leading-tight">
            {t('Prüfungssimulation')}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-snug">
            {t('30 Fragen. Max. 10 Fehlerpunkte.')}
          </p>
        </div>
      </div>

      {/* Primary CTA — placed early so it is reachable without scrolling */}
      <Button
        size="lg"
        fullWidth
        onClick={() => {
          if (!isPremium && user.simulationAttempts >= 3) {
            setPremiumTrigger('exam');
            return;
          }
          onStartExam();
        }}
        className="shadow-lg shadow-orange-200 dark:shadow-none"
      >
        {!isPremium && user.simulationAttempts >= 3
          ? t('Premium freischalten')
          : t('Jetzt starten')}
      </Button>

      {/* Attempts indicator — single compact line */}
      <div className="glass-card px-3 py-2 w-full flex items-center justify-between text-xs">
        <span className="text-[var(--f-text-muted)]">{t('Bisherige Versuche')}</span>
        <span className="font-bold text-[var(--f-text)]">
          {user.simulationAttempts}
          {!isPremium && (
            <span className="text-gray-400 font-normal">/3 {t('kostenlose Versuche')}</span>
          )}
        </span>
      </div>

      {/* F-06: a free account draws from its unlocked questions only, so
          simulations repeat and are not representative. Say so instead of
          letting the score create false confidence. */}
      {!isPremium && (
        <div className="glass-card px-3 py-2 w-full flex items-start gap-2 text-left">
          <Info size={14} className="text-primary shrink-0 mt-0.5" />
          <p className="text-xs text-[var(--f-text-muted)] leading-snug">
            {t('exam_free_pool_hint', { count: examPoolSize })}
          </p>
        </div>
      )}

      <ReadinessCard user={user} compact />

      {trend && (
        <div className="w-full glass-card px-3 py-2 text-center text-xs">
          <span className="text-gray-400 font-bold mr-2">{t('confidence_trend_title')}</span>
          <span>{trend}</span>
        </div>
      )}

      {history.length > 0 && (
        <details className="w-full glass-card overflow-hidden">
          <summary className="text-xs font-bold text-[var(--f-text-muted)] px-3 py-2 cursor-pointer flex items-center justify-between">
            <span>{t('Bisherige Ergebnisse')}</span>
            <span className="text-[10px] text-gray-400 font-normal">{history.length}</span>
          </summary>
          <div className="divide-y divide-gray-50 dark:divide-gray-800 max-h-60 overflow-y-auto">
            {[...history]
              .reverse()
              .slice(0, 10)
              .map((sim, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      sim.passed
                        ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                    }`}
                  >
                    {sim.passed ? '✓' : '✗'}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-xs font-semibold text-[var(--f-text)] truncate">
                      {sim.passed ? t('Bestanden!') : t('Nicht bestanden')}
                    </div>
                    <div className="text-[10px] text-gray-400">
                      {new Date(sim.date).toLocaleDateString(
                        user.language === 'de' ? 'de-DE' : 'en-US',
                        {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        }
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-bold text-[var(--f-text)]">
                      {sim.correctCount}/30
                    </div>
                    <div
                      className={`text-[10px] font-semibold ${sim.errorPoints <= 10 ? 'text-green-500' : 'text-red-500'}`}
                    >
                      {sim.errorPoints} {t('Fehlerpunkte')}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </details>
      )}
    </div>
  );
};

export default ExamTab;
